import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Stack,
    Typography
} from '@mui/material'

import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import React from 'react'
import { makeStyles } from 'tss-react/mui'
import { NotificationStyle } from 'src/modules/admin/configurations/scopes'
import { DateFns } from 'src/utils/date-fns'
import { parseLocalDateNoTZ } from 'src/utils/formatter-utils'
import { FCClassContext, classToFComponent } from 'src/utils/views'
import { NotificationsScope } from '../../Main.scopes'
import { Logger } from 'wdc-cube'

const LOG = Logger.get('NotificationModal')

const useStyles = makeStyles()((theme) => ({
    dialogRoot: {
        '& .MuiDialog-container': {
            alignItems: 'flex-start',
            marginTop: theme.spacing(8)
        }
    },

    dialogPaper: {
        width: 720,
        maxWidth: '95%',
        borderRadius: 16
    },

    headerContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    title: {
        fontWeight: 700,
        color: theme.palette.error.main
    },

    contentContainer: {
        padding: theme.spacing(2)
    },

    boxContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },

    width100: {
        width: '100%'
    },

    chipBorder: {
        borderRadius: 6
    },

    accordion: {
        marginBottom: theme.spacing(2),
        border: '1px solid #e0e0e0',
        borderRadius: '12px !important',
        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
        '&:before': {
            display: 'none'
        }
    },

    accordionDetails: {
        backgroundColor: '#f9fafb',
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12
    },

    badgeIconWrapper: {
        marginTop: theme.spacing(0.5)
    },

    notificationSummary: {
        display: 'block'
    },

    notificationDate: {
        marginTop: theme.spacing(1)
    },

    notificationContent: {
        marginBottom: theme.spacing(2)
    },

    footerContainer: {
        marginTop: theme.spacing(2),
        display: 'flex',
        justifyContent: 'flex-end'
    }
}))

export type NotificationModalProps = {
    scope: NotificationsScope
}

class NotificationModalClass implements FCClassContext<NotificationModalProps> {
    scope!: NotificationsScope

    readonly onClose = () => this.scope.onAlertModalClose().catch(LOG.caught)

    readonly emitMarkAsRead = (id: number) => this.scope.onMarkNotificationAsRead(id).catch(LOG.caught)

    getIcon = (type: NotificationStyle) => {
        switch (type) {
            case NotificationStyle.ERROR:
                return <ErrorOutlineIcon sx={{ color: '#d32f2f' }} />
            case NotificationStyle.WARNING:
                return <WarningAmberIcon sx={{ color: '#ed6c02' }} />
            case NotificationStyle.SUCCESS:
                return <CheckCircleOutlineIcon sx={{ color: '#2e7d32' }} />
            default:
                return <InfoOutlinedIcon sx={{ color: '#0288d1' }} />
        }
    }

    getColorText = (type: NotificationStyle) => {
        switch (type) {
            case NotificationStyle.ERROR:
                return 'error'
            case NotificationStyle.WARNING:
                return 'warning'
            case NotificationStyle.SUCCESS:
                return 'success'
            case NotificationStyle.INFO:
                return 'info'
        }
    }

    getColor = (type: NotificationStyle) => {
        switch (type) {
            case NotificationStyle.ERROR:
                return '#ef5350'
            case NotificationStyle.WARNING:
                return '#fbc02d'
            case NotificationStyle.SUCCESS:
                return '#4caf50'
            case NotificationStyle.INFO:
                return '#42a5f5'
        }
    }

    onSyncState({ scope }: NotificationModalProps) {
        this.scope = scope
    }

    render() {
        const { classes } = useStyles()

        const alerts = this.scope.alerts

        return (
            <Dialog
                open={this.scope.alertModalOpen}
                onClose={this.onClose}
                classes={{ root: classes.dialogRoot, paper: classes.dialogPaper }}
            >
                <DialogTitle>
                    <Box className={classes.headerContainer}>
                        <Typography variant="h6" className={classes.title}>
                            Alertas
                        </Typography>
                        <IconButton onClick={this.onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>

                {this.scope.waiting && <LinearProgress color="error" />}

                <DialogContent className={classes.contentContainer}>
                    {alerts.map((notif) => (
                        <Accordion
                            key={notif.id}
                            elevation={0}
                            className={classes.accordion}
                            style={{
                                borderLeft: `5px solid ${this.getColor(notif.notificationStyle)}`
                            }}
                        >
                            <AccordionSummary className={classes.width100} expandIcon={<ExpandMoreIcon />}>
                                <Stack
                                    className={classes.width100}
                                    direction="row"
                                    spacing={2}
                                    sx={{
                                        alignItems: 'flex-start'
                                    }}
                                >
                                    <Box className={classes.badgeIconWrapper}>
                                        {this.getIcon(notif.notificationStyle)}
                                    </Box>

                                    <Box className={classes.width100}>
                                        <Box className={classes.boxContainer}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {notif.title}
                                            </Typography>
                                            {notif.type && (
                                                <Chip
                                                    size="small"
                                                    label={notif.type}
                                                    color="secondary"
                                                    variant="outlined"
                                                    className={classes.chipBorder}
                                                />
                                            )}
                                        </Box>

                                        <Typography
                                            variant="caption"
                                            className={classes.notificationSummary}
                                            sx={{
                                                color: 'text.secondary'
                                            }}
                                        >
                                            {notif.summary}
                                        </Typography>

                                        {notif.notificationDate && (
                                            <Stack direction="row" spacing={1} className={classes.notificationDate}>
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.disabled'
                                                    }}
                                                >
                                                    {DateFns.formatDate(
                                                        parseLocalDateNoTZ(notif.notificationDate.toString()),
                                                        'dd/MM/yyyy'
                                                    )}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                </Stack>
                            </AccordionSummary>

                            <AccordionDetails className={classes.accordionDetails}>
                                <Typography
                                    variant="body2"
                                    className={classes.notificationContent}
                                    dangerouslySetInnerHTML={{
                                        __html: notif.content
                                    }}
                                    sx={{
                                        color: 'text.secondary'
                                    }}
                                />

                                {notif.canMarkAsRead && (
                                    <Button
                                        variant="outlined"
                                        color={this.getColorText(notif.notificationStyle)}
                                        size="small"
                                        fullWidth
                                        startIcon={<TaskAltIcon />}
                                        disabled={!!notif.readAt}
                                        onClick={() => this.emitMarkAsRead(notif.id)}
                                    >
                                        Marcar como Lida
                                    </Button>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))}

                    <Box className={classes.footerContainer}>
                        <Button variant="outlined" onClick={this.onClose}>
                            Fechar
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        )
    }
}

export default classToFComponent(NotificationModalClass)
